import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import usePageTracker from "@/hooks/usePageTracker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Car, LogOut, Calendar, Users, Phone, MapPin, 
  Clock, Navigation, Share2, MessageCircle, MessageSquare, Plane,
  ChevronRight, RefreshCw, CheckCircle, Loader2, Eye, EyeOff, BellRing, Bell, Download, Luggage, Package,
  DollarSign
} from "lucide-react";
import { fmtChicagoTime } from "@/utils/timezone";
import { getAirlineColors, getAirlineCode } from "@/utils/airlineUtils";
import { subscribeToPush, isPushSupported } from "@/utils/pushNotifications";
import DriverGpsMap from "@/components/driver/DriverGpsMap";
import DriverPaymentModal from "@/components/driver/DriverPaymentModal";
import FlightStatusBadge from "@/components/driver/FlightStatusBadge";
import { AdminInstallPrompt } from "@/components/AdminInstallPrompt";
import { DriverPayoutsTab } from "@/components/DriverPayoutsTab";

import getApiUrl from "@/utils/api";
import { format12Hour } from "@/utils/time";
import { getPickupTimeStyle } from "@/utils/pickupTimeColor";

const API = getApiUrl();

export default function DriverApp() {
  usePageTracker("Driver App");
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);
  const [view, setView] = useState("trips");
  const [trips, setTrips] = useState(null);
  const [passengerList, setPassengerList] = useState(null);
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationShared, setLocationShared] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [appInstalled, setAppInstalled] = useState(false);
  
  // Filter state for driver
  const [statusFilter, setStatusFilter] = useState(null); // "on_flight", "delayed", "missed_flight", null (all)

  // Payment modal
  const [paymentBooking, setPaymentBooking] = useState(null);
  const [driverProfile, setDriverProfile] = useState(null); // Holds cashapp_tag/zelle_email for QR

  // Push notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  useEffect(() => {
    if (isPushSupported() && Notification.permission === 'granted') {
      setPushEnabled(true);
    }
  }, []);
  const enablePushNotifications = async () => {
    if (!isPushSupported()) {
      toast.error("Push notifications not supported on this browser");
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        await subscribeToPush('driver', driverInfo?.id);
        setPushEnabled(true);
        toast.success("Push notifications enabled! You'll be alerted when assigned to trips.");
      } else {
        toast.error("Permission denied. Enable notifications in browser settings.");
      }
    } catch (e) {
      toast.error("Failed to enable notifications");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("driver_token");
    const driver = localStorage.getItem("driver_info");
    if (token && driver) {
      const driverData = JSON.parse(driver);
      setIsLoggedIn(true);
      setDriverInfo(driverData);
      fetchTrips(token);
      // Auto-subscribe to push notifications
      if (isPushSupported() && Notification.permission === 'granted') {
        subscribeToPush('driver', driverData.id);
      }
    }
    
    // PWA install prompt
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    setAppInstalled(isStandalone);
    
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/driver/login`, { phone: phone.trim(), pin: pin.trim() });
      localStorage.setItem("driver_token", res.data.token);
      localStorage.setItem("driver_info", JSON.stringify(res.data.driver));
      setDriverInfo(res.data.driver);
      setIsLoggedIn(true);
      fetchTrips(res.data.token);
      fetchDriverProfile(res.data.token);
      toast.success(`Welcome, ${res.data.driver.name}!`);
      // Subscribe to push notifications
      if (isPushSupported()) {
        subscribeToPush('driver', res.data.driver.id);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Connection error";
      toast.error(`Login failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("driver_token");
    localStorage.removeItem("driver_info");
    setIsLoggedIn(false);
    setDriverInfo(null);
    setTrips(null);
  };

  const fetchTrips = async (token) => {
    try {
      const res = await axios.get(`${API}/driver/trips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrips(res.data);
    } catch (error) {
      console.error("Failed to fetch trips:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchDriverProfile = async (token) => {
    try {
      const res = await axios.get(`${API}/driver/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriverProfile(res.data);
    } catch {
      // Non-fatal — payment modal will fall back to phone-only QR
    }
  };

  const fetchPassengerList = async () => {
    const token = localStorage.getItem("driver_token");
    try {
      const res = await axios.get(`${API}/driver/passenger-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPassengerList(res.data);
    } catch (error) {
      toast.error("Failed to get passenger list");
    }
  };

  const shareToWhatsApp = () => {
    if (passengerList?.whatsapp_share_url) {
      window.open(passengerList.whatsapp_share_url, '_blank');
    } else {
      fetchPassengerList().then(() => {
        if (passengerList?.whatsapp_share_url) {
          window.open(passengerList.whatsapp_share_url, '_blank');
        }
      });
    }
  };

  const updateLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const token = localStorage.getItem("driver_token");
        try {
          await axios.post(
            `${API}/driver/update-location?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setLocationShared(true);
          toast.success("Location shared with passengers!");
        } catch (error) {
          toast.error("Failed to update location");
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        toast.error("Could not get location: " + error.message);
        setLocationLoading(false);
      }
    );
  };

  const hideLocation = async () => {
    const token = localStorage.getItem("driver_token");
    try {
      await axios.post(`${API}/driver/hide-location`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocationShared(false);
      toast.success("Location hidden from passengers");
    } catch (error) {
      toast.error("Failed to hide location");
    }
  };

  const navigateWithWaze = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://waze.com/ul?q=${encoded}&navigate=yes`, '_blank');
  };

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setAppInstalled(true);
        toast.success("App installed!");
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: show manual instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        toast.info("Tap the Share button in Safari, then 'Add to Home Screen'");
      } else {
        toast.info("Tap the browser menu (⋮) and select 'Install app' or 'Add to Home Screen'");
      }
    }
  };

  const navigateToPickup = async (booking) => {
    const token = localStorage.getItem("driver_token");
    // Notify passenger via WhatsApp that driver is on the way
    if (!booking.driver_enroute) {
      try {
        const res = await axios.post(`${API}/driver/notify-enroute/${booking.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          toast.success(res.data.message);
        }
      } catch (error) {
        console.error("Failed to notify passenger:", error);
      }
    }
    // Open Waze navigation
    navigateWithWaze(booking.pickup_location || "Chicago O'Hare International Airport");
  };

  const callPassenger = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const whatsappPassenger = (phone) => {
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Car className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Driver Login</CardTitle>
            <p className="text-muted-foreground">Easy Transportation</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Phone Number <span className="text-red-500">*</span></Label>
                <Input
                  type="tel"
                  placeholder="+1 (xxx) xxx-xxxx"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-12"
                  required
                  data-testid="driver-phone-input"
                />
              </div>
              <div>
                <Label>PIN <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    type={showPin ? "text" : "password"}
                    placeholder="Enter PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    className="h-12 pr-12"
                    data-testid="driver-pin-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-pin-visibility"
                  >
                    {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 bg-green-600 hover:bg-green-700"
                disabled={loading}
                data-testid="driver-login-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Trip Card Component
  const TripCard = ({ booking, index }) => {
    const airlineCode = getAirlineCode(booking.flight_number);
    const colors = getAirlineColors(airlineCode);
    const flightStatusInfo = booking.flight_status_info || (booking.flight_status ? {
      status: booking.flight_status,
      landing_time: booking.landing_time || '',
      terminal: booking.arrival_terminal || '',
      gate: booking.arrival_gate || '',
      baggage: booking.arrival_baggage || '',
      delay: booking.delay_minutes || null
    } : null);
    
    // Passenger status configuration
    const statusConfig = {
      on_flight: { label: "On Flight", bg: "bg-green-100", text: "text-green-700", icon: "✈️" },
      missed_flight: { label: "Missed Flight", bg: "bg-red-100", text: "text-red-700", icon: "❌" },
      delayed: { label: `Delayed ${booking.delay_minutes ? booking.delay_minutes + 'min' : ''}`, bg: "bg-yellow-100", text: "text-yellow-700", icon: "⏰" }
    };
    const passengerStatus = booking.passenger_status ? statusConfig[booking.passenger_status] : null;

    // Flight status color map
    const flightStatusColor = {
      landed: "bg-green-100 text-green-700 border-green-300",
      active: "bg-blue-100 text-blue-700 border-blue-300",
      delayed: "bg-red-100 text-red-700 border-red-300",
      scheduled: "bg-gray-100 text-gray-600 border-gray-300",
      cancelled: "bg-red-200 text-red-800 border-red-400",
    };
    const flightStatusLabel = {
      landed: "Landed", active: "In Air", delayed: "Delayed", scheduled: "Scheduled", cancelled: "Cancelled"
    };

    // Get all passengers and their drop-off locations
    const passengersList = booking.passengers || [];
    
    return (
      <Card className={`mb-3 ${passengerStatus?.bg || ''}`} data-testid={`trip-card-${index}`}>
        <CardContent className="p-4">
          {/* Passenger Status Badge */}
          {passengerStatus && (
            <div className={`mb-3 px-3 py-2 rounded-lg ${passengerStatus.bg} ${passengerStatus.text} flex items-center gap-2 font-medium text-sm`}>
              <span>{passengerStatus.icon}</span>
              <span>{passengerStatus.label}</span>
              {booking.check_in_time && (
                <span className="text-xs opacity-70 ml-auto">
                  {fmtChicagoTime(booking.check_in_time)} CT
                </span>
              )}
            </div>
          )}

          {/* Deposit / Payment Status Badge */}
          {(() => {
            const total = (booking.trip_price || 0) + (booking.luggage_fee || 0);
            const paid = booking.amount_paid || 0;
            const balance = Math.max(0, total - paid);
            const depositVerified = booking.deposit_verified || paid >= 20;
            const fullyPaid = booking.payment_status === "paid" || balance <= 0.01;
            return (
              <div
                className={`mb-3 px-3 py-2 rounded-lg border flex items-center justify-between text-sm ${
                  fullyPaid
                    ? "bg-green-50 border-green-300 text-green-800"
                    : depositVerified
                    ? "bg-amber-50 border-amber-300 text-amber-900"
                    : "bg-rose-50 border-rose-300 text-rose-900"
                }`}
                data-testid={`payment-badge-${booking.id}`}
              >
                <div className="flex items-center gap-2 font-medium">
                  <DollarSign className="w-4 h-4" />
                  {fullyPaid
                    ? "Fully Paid"
                    : depositVerified
                    ? `Deposit Paid · $${balance.toFixed(0)} due`
                    : `Deposit Pending · $${total.toFixed(0)} due`}
                </div>
                {!fullyPaid && (
                  <Button
                    size="sm"
                    onClick={() => setPaymentBooking(booking)}
                    className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                    data-testid={`take-payment-btn-${booking.id}`}
                  >
                    Take Payment
                  </Button>
                )}
              </div>
            );
          })()}

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-white font-bold text-xs ${colors.bg}`}>
                  {airlineCode || '??'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{booking.full_name}</p>
                  {booking.whatsapp_number && (() => {
                    const clean = (booking.whatsapp_number || "").replace(/[^0-9]/g, "");
                    return (
                      <a href={`https://wa.me/${clean}`} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:underline flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />{booking.whatsapp_number}
                      </a>
                    );
                  })()}
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`text-xs font-bold px-2 py-0.5 rounded border ${getPickupTimeStyle(booking.travel_time).badge}`}
                      title={getPickupTimeStyle(booking.travel_time).label}
                      data-testid={`driver-pickup-time-${booking.id}`}
                    >
                      {getPickupTimeStyle(booking.travel_time).emoji} {format12Hour(booking.travel_time) || booking.travel_time}
                    </span>
                    <p className="text-sm text-muted-foreground">{booking.flight_number}</p>
                    {booking.flight_number && (
                      <FlightStatusBadge
                        flightNumber={booking.flight_number}
                        travelDate={booking.travel_date}
                        terminal={booking.arrival_terminal}
                      />
                    )}
                  </div>
                  {booking.travel_date && (() => {
                    const d = new Date(booking.travel_date + "T12:00:00");
                    if (isNaN(d)) return null;
                    const wd = d.toLocaleDateString('en-US', { weekday: 'long' });
                    return (
                      <p
                        className="text-xs text-blue-700 font-medium mt-0.5"
                        data-testid={`trip-day-date-${booking.id}`}
                      >
                        {wd}, {booking.travel_date}
                      </p>
                    );
                  })()}
                </div>
              </div>

              {/* Flight Status Info */}
              {flightStatusInfo && (
                <div className={`mb-2 px-2.5 py-1.5 rounded-lg border text-xs flex flex-wrap items-center gap-2 ${flightStatusColor[flightStatusInfo.status] || flightStatusColor.scheduled}`}>
                  <Plane className="w-3.5 h-3.5" />
                  <span className="font-semibold">{flightStatusLabel[flightStatusInfo.status] || flightStatusInfo.status}</span>
                  {flightStatusInfo.landing_time && <span>ETA: {flightStatusInfo.landing_time}</span>}
                  {flightStatusInfo.terminal && <span>T{flightStatusInfo.terminal}</span>}
                  {flightStatusInfo.gate && <span>Gate {flightStatusInfo.gate}</span>}
                  {flightStatusInfo.baggage && <span>Bag {flightStatusInfo.baggage}</span>}
                  {flightStatusInfo.delay && <span className="text-red-600 font-bold">+{flightStatusInfo.delay}min</span>}
                </div>
              )}
              
              <div className="space-y-1 text-sm">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="truncate">{booking.dropoff_location || booking.housing_unit || booking.housing_address}</span>
                    {booking.housing_address && booking.housing_address !== booking.dropoff_location && booking.housing_address !== booking.housing_unit && (
                      <p className="text-xs text-blue-600">{booking.housing_address}</p>
                    )}
                  </div>
                </div>
                {booking.trip_type === "return" && booking.pickup_location && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span>Pickup: {booking.pickup_location}</span>
                      {booking.housing_address && booking.trip_type === "return" && (
                        <p className="text-xs text-blue-600">{booking.housing_address}</p>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span>{booking.num_passengers} passenger{booking.num_passengers > 1 ? 's' : ''}</span>
                </div>
                {(booking.total_suitcases > 0) && (
                  <div className="flex items-center gap-2">
                    <Luggage className="w-4 h-4 text-amber-600" />
                    <span className="text-amber-700 font-medium">{booking.total_suitcases} suitcase{booking.total_suitcases > 1 ? 's' : ''}</span>
                  </div>
                )}
                {(booking.total_carry_ons > 0) && (
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700 font-medium">{booking.total_carry_ons} carry-on{booking.total_carry_ons > 1 ? 's' : ''}</span>
                  </div>
                )}
                {booking.linked_shipping && booking.linked_shipping.length > 0 && (
                  <div className="mt-2 p-2.5 bg-orange-50 rounded-lg border border-orange-200" data-testid={`shipping-info-${booking.id}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Package className="w-3.5 h-3.5 text-orange-600" />
                      <span className="text-xs font-bold text-orange-800">Linked Shipping</span>
                    </div>
                    {booking.linked_shipping.map((ship) => {
                      const itemsList = Object.entries(ship.items || {}).filter(([, v]) => v > 0);
                      return (
                        <div key={ship.id} className="text-xs text-orange-700">
                          <div className="flex flex-wrap gap-1.5">
                            {itemsList.map(([item, count]) => (
                              <Badge key={item} variant="secondary" className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0">
                                {count}x {item}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-[10px] text-orange-500 mt-0.5">{ship.total_items} items total | #{ship.id}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                {booking.trip_type === "return" && booking.departure_time && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-500" />
                    <span className="text-orange-700 font-medium">Departure: {booking.departure_time}</span>
                  </div>
                )}
                {booking.check_in_notes && (
                  <div className="flex items-start gap-2 mt-2 p-2 bg-gray-100 rounded">
                    <span className="text-xs text-gray-600">Note: {booking.check_in_notes}</span>
                  </div>
                )}
              </div>

              {/* Per-passenger drop-off locations */}
              {passengersList.length > 1 && (
                <div className="mt-2 pt-2 border-t border-dashed border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Passengers & Drop-offs:</p>
                  {passengersList.map((p, pi) => (
                    <div key={pi} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold">{pi+1}</span>
                      <span className="font-medium">{p.name || p.full_name || `Passenger ${pi+1}`}</span>
                      {p.whatsapp && (() => {
                        const clean = (p.whatsapp || "").replace(/[^0-9]/g, "");
                        return (
                          <a href={`https://wa.me/${clean}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline flex items-center gap-0.5">
                            <MessageSquare className="w-2.5 h-2.5" />{p.whatsapp}
                          </a>
                        );
                      })()}
                      {(p.dropoff_location || p.destination || p.custom_address) && (
                        <span className="text-muted-foreground">→ {p.dropoff_location || p.destination || p.custom_address}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex flex-col gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => callPassenger(booking.whatsapp_number)}
                className="h-9"
              >
                <Phone className="w-4 h-4" />
              </Button>
              <Button 
                size="sm"
                className="h-9 bg-green-600 hover:bg-green-700"
                onClick={() => whatsappPassenger(booking.whatsapp_number)}
              >
                <MessageCircle className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Waze Navigation Buttons */}
          <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t">
            <Button 
              size="sm" 
              variant="outline"
              className="h-9 text-xs bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              onClick={() => navigateToPickup(booking)}
              data-testid={`waze-pickup-${index}`}
            >
              <Navigation className="w-3 h-3 mr-1" />
              {booking.driver_enroute ? "Navigate to Pickup" : "On My Way"}
            </Button>
            {(booking.dropoff_location || booking.housing_address) ? (
              <Button 
                size="sm" 
                variant="outline"
                className="h-9 text-xs bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                onClick={() => navigateWithWaze(booking.housing_address || booking.dropoff_location)}
                data-testid={`waze-housing-${index}`}
              >
                <MapPin className="w-3 h-3 mr-1" />
                Navigate to Housing
              </Button>
            ) : <div />}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Main Driver Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      <AdminInstallPrompt
        storageKey="driverInstallSnoozedUntil"
        title="Install Driver App"
        description="Install the Driver app on your phone for one-tap access to passengers, GPS, and trip details."
        headerBg="bg-gradient-to-r from-green-600 to-emerald-700"
      />
      {/* Header */}
      <div className="bg-green-600 text-white p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Car className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold">{driverInfo?.name}</p>
              <p className="text-sm text-white/80">Driver</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!pushEnabled && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={enablePushNotifications}
                className="text-white hover:bg-white/20"
                data-testid="driver-enable-push-btn"
                title="Enable trip notifications"
              >
                <BellRing className="w-5 h-5" />
              </Button>
            )}
            {!appInstalled && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleInstallApp}
                className="text-white hover:bg-white/20"
                data-testid="driver-install-btn"
              >
                <Download className="w-5 h-5" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-white hover:bg-white/20"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* View Switcher */}
      {!pushEnabled && isPushSupported() && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between gap-3" data-testid="driver-push-banner">
          <div className="flex items-center gap-2 min-w-0">
            <BellRing className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-800">Enable Trip Alerts</p>
              <p className="text-xs text-amber-600 truncate">Get notified instantly when assigned to a new trip</p>
            </div>
          </div>
          <Button size="sm" onClick={enablePushNotifications} className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0" data-testid="driver-push-enable-btn">
            <Bell className="w-4 h-4 mr-1" /> Enable
          </Button>
        </div>
      )}
      <div className="bg-white border-b px-4 py-2 flex gap-2">
        <Button
          variant={view === "trips" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("trips")}
          className={`flex-1 ${view === "trips" ? "bg-green-600 hover:bg-green-700" : ""}`}
          data-testid="driver-view-trips"
        >
          <Calendar className="w-4 h-4 mr-1.5" /> My Trips
        </Button>
        <Button
          variant={view === "payouts" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("payouts")}
          className={`flex-1 ${view === "payouts" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
          data-testid="driver-view-payouts"
        >
          <DollarSign className="w-4 h-4 mr-1.5" /> Payouts
        </Button>
      </div>

      {view === "payouts" ? (
        <div className="p-4">
          <DriverPayoutsTab driverToken={localStorage.getItem("driver_token")} />
        </div>
      ) : (
        <>
      {/* Quick Actions */}
      <div className="p-4 bg-white border-b">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Button 
            variant={locationShared ? "default" : "outline"}
            className={`h-16 flex-col ${locationShared ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
            onClick={updateLocation}
            disabled={locationLoading}
            data-testid="share-location-btn"
          >
            {locationLoading ? (
              <Loader2 className="w-5 h-5 animate-spin mb-1" />
            ) : (
              <Navigation className="w-5 h-5 mb-1" />
            )}
            <span className="text-xs">{locationShared ? "Location Shared" : "Share Location"}</span>
          </Button>
          {locationShared && (
            <Button 
              variant="outline"
              className="h-16 flex-col border-red-200 text-red-600 hover:bg-red-50"
              onClick={hideLocation}
              data-testid="hide-location-btn"
            >
              <EyeOff className="w-5 h-5 mb-1" />
              <span className="text-xs">Hide Location</span>
            </Button>
          )}
          {!locationShared && (
            <Button 
              variant="outline" 
              className="h-16 flex-col"
              onClick={() => {
                fetchPassengerList();
                shareToWhatsApp();
              }}
              data-testid="share-list-btn"
            >
              <Share2 className="w-5 h-5 mb-1 text-green-600" />
              <span className="text-xs">Share Passenger List</span>
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline" 
            className="h-12 flex-col"
            onClick={() => fetchTrips(localStorage.getItem("driver_token"))}
          >
            <RefreshCw className="w-4 h-4 mb-1 text-purple-600" />
            <span className="text-xs">Refresh Trips</span>
          </Button>
        </div>
      </div>

      {/* Trips */}
      <div className="p-4">
        {/* Internal GPS Map - shows driver location + next booking pins */}
        <div className="mb-4">
          <DriverGpsMap bookings={(trips?.today?.bookings || []).concat(trips?.tomorrow?.bookings || [])} />
        </div>

        {(() => {
          // Build day buckets: prefer `days` array; fall back to legacy today/tomorrow.
          const dayBuckets = trips?.days && trips.days.length > 0
            ? trips.days
            : [
                trips?.today ? { ...trips.today, label: "Today" } : null,
                trips?.tomorrow ? { ...trips.tomorrow, label: "Tomorrow" } : null,
              ].filter(Boolean);

          if (dayBuckets.length === 0) return null;

          const badgeColors = ["bg-green-600", "bg-blue-600", "bg-purple-600", "bg-amber-600", "bg-pink-600"];
          const cardColors = [
            { bg: "bg-green-50", border: "border-green-200", icon: "text-green-600", empty: "text-green-300" },
            { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600", empty: "text-blue-300" },
            { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600", empty: "text-purple-300" },
            { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-600", empty: "text-amber-300" },
            { bg: "bg-pink-50", border: "border-pink-200", icon: "text-pink-600", empty: "text-pink-300" },
          ];

          return (
            <Tabs defaultValue={`day-0`}>
              <TabsList className="w-full mb-4 overflow-x-auto flex-nowrap">
                {dayBuckets.map((day, idx) => {
                  const hasTrips = (day.total_passengers || 0) > 0;
                  return (
                    <TabsTrigger
                      key={day.date || idx}
                      value={`day-${idx}`}
                      className={`flex-1 min-w-[80px] text-xs sm:text-sm relative ${
                        hasTrips
                          ? "ring-2 ring-emerald-300 ring-offset-1 font-semibold data-[state=active]:ring-emerald-500"
                          : ""
                      }`}
                      data-testid={`driver-day-tab-${idx}`}
                    >
                      {hasTrips && (
                        <span
                          className="absolute top-0.5 right-0.5 flex h-2 w-2"
                          data-testid={`driver-day-tab-dot-${idx}`}
                          aria-label="has pickups"
                        >
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                      )}
                      <span className="truncate">{day.label || day.date}</span>
                      {hasTrips && (
                        <Badge className={`ml-1.5 ${badgeColors[idx % badgeColors.length]} text-[10px] px-1.5 py-0`}>
                          {day.total_passengers}
                        </Badge>
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {dayBuckets.map((day, idx) => {
                const c = cardColors[idx % cardColors.length];
                const isToday = idx === 0;
                return (
                  <TabsContent key={day.date || idx} value={`day-${idx}`}>
                    <div className={`mb-3 p-3 ${c.bg} rounded-lg border ${c.border}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className={`w-5 h-5 ${c.icon}`} />
                          <span className="font-medium">{day.label || ""} - {day.date}</span>
                        </div>
                        <Badge variant="outline" className="bg-white">
                          {day.total_passengers || 0} passengers
                        </Badge>
                      </div>
                      {((day.total_suitcases || 0) > 0 || (day.total_carry_ons || 0) > 0) && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge variant="outline" className="bg-white text-amber-700 border-amber-300" data-testid={`driver-day-suitcases-${idx}`}>
                            🧳 {day.total_suitcases || 0} suitcase{(day.total_suitcases || 0) !== 1 ? "s" : ""}
                          </Badge>
                          <Badge variant="outline" className="bg-white text-blue-700 border-blue-300" data-testid={`driver-day-carryons-${idx}`}>
                            🎒 {day.total_carry_ons || 0} carry-on{(day.total_carry_ons || 0) !== 1 ? "s" : ""}
                          </Badge>
                        </div>
                      )}
                      {/* Late-night operational notice: between midnight and 4 AM CT, "today" still refers to yesterday's manifest */}
                      {isToday && (() => {
                        try {
                          const nowChi = new Date().toLocaleString("en-US", { timeZone: "America/Chicago", hour: "2-digit", hour12: false });
                          const hr = parseInt(nowChi, 10);
                          return hr >= 0 && hr < 4 ? (
                            <p className="text-[11px] text-green-800 mt-2" data-testid="late-night-notice">
                              🌙 It&apos;s after midnight — showing yesterday&apos;s manifest until 4 AM CT.
                            </p>
                          ) : null;
                        } catch { return null; }
                      })()}
                    </div>

                    {/* Status Filter Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge
                        className={`cursor-pointer text-xs ${!statusFilter ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'}`}
                        onClick={() => setStatusFilter(null)}
                      >
                        All
                      </Badge>
                      <Badge
                        className={`cursor-pointer text-xs ${statusFilter === 'on_flight' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700'}`}
                        onClick={() => setStatusFilter(statusFilter === 'on_flight' ? null : 'on_flight')}
                      >
                        ✈️ On Flight
                      </Badge>
                      <Badge
                        className={`cursor-pointer text-xs ${statusFilter === 'delayed' ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-700'}`}
                        onClick={() => setStatusFilter(statusFilter === 'delayed' ? null : 'delayed')}
                      >
                        ⏰ Delayed
                      </Badge>
                      {isToday && (
                        <Badge
                          className={`cursor-pointer text-xs ${statusFilter === 'missed_flight' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'}`}
                          onClick={() => setStatusFilter(statusFilter === 'missed_flight' ? null : 'missed_flight')}
                        >
                          ❌ Missed
                        </Badge>
                      )}
                      <Badge
                        className={`cursor-pointer text-xs ${statusFilter === 'not_checked_in' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'}`}
                        onClick={() => setStatusFilter(statusFilter === 'not_checked_in' ? null : 'not_checked_in')}
                      >
                        📋 Not Checked In
                      </Badge>
                    </div>

                    {(() => {
                      let dayBookings = [
                        ...(day.individual || []),
                        ...(day.group || []).map(b => ({ ...b, full_name: b.organizer_name })),
                      ];

                      if (statusFilter === 'not_checked_in') {
                        dayBookings = dayBookings.filter(b => !b.passenger_status);
                      } else if (statusFilter) {
                        dayBookings = dayBookings.filter(b => b.passenger_status === statusFilter);
                      }

                      dayBookings.sort((a, b) => (a.travel_time || '').localeCompare(b.travel_time || ''));

                      if (dayBookings.length === 0) {
                        return (
                          <div className="text-center py-8 text-muted-foreground">
                            <CheckCircle className={`w-12 h-12 mx-auto mb-3 ${c.empty}`} />
                            <p>{statusFilter ? 'No trips match this filter' : `No trips scheduled for ${(day.label || day.date).toLowerCase()}`}</p>
                          </div>
                        );
                      }

                      return dayBookings.map((booking, i) => (
                        <TripCard key={booking.id} booking={booking} index={i} />
                      ));
                    })()}
                  </TabsContent>
                );
              })}
            </Tabs>
          );
        })()}
      </div>
        </>
      )}

      {/* Passenger List Preview */}
      {passengerList && view === "trips" && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Passenger List Ready</p>
              <p className="text-sm text-muted-foreground">
                Today: {passengerList.today_count} | Tomorrow: {passengerList.tomorrow_count}
              </p>
            </div>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={shareToWhatsApp}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share via WhatsApp
            </Button>
          </div>
        </div>
      )}

      {/* Driver Payment Modal */}
      <DriverPaymentModal
        open={!!paymentBooking}
        onClose={() => setPaymentBooking(null)}
        booking={paymentBooking}
        driver={driverProfile || driverInfo}
        API={API}
        token={localStorage.getItem("driver_token")}
        onPaid={() => fetchTrips(localStorage.getItem("driver_token"))}
      />
    </div>
  );
}
